import { ColorPalette } from "@flat-ui";
import { FaroSwitch } from "@components/common/faro-switch";
import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { SphereTooltipIcon } from "@components/common/sphere-tooltip-icon";
import { PageSectionProps } from "@components/page-section/page-section";
import { useAppServer } from "@hooks/use-app-server";
import { useBrandingSettings } from "@hooks/use-branding-settings";
import { Box, SvgIcon } from "@mui/material";
import { BaseSettingsPage } from "@pages/settings/base-settings-page";
import { useDefaultPlatformSection } from "@pages/settings/general-settings/use-default-platform-section";
import { useWaypointColor } from "@hooks/use-waypoint-color";
import { WorkspaceLogoSetting } from "@pages/settings/general-settings/workspace-logo-setting";
import { appStateSelector } from "@store/app/app-selector";
import {
  defaultProjectLaunchTargetSelector,
  fetchingSdbCompanyFlagsSelector,
  isFetchingSdbCompanySelector,
  selectedSdbCompanySelector,
} from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { inputPlaceholderItalicSx, withEllipsis } from "@styles/common-styles";
import { useEffect } from "react";
import { InfoIcon } from "@flat-ui";
import { WAYPOINT_COLOR_PALETTE } from "@constants/waypoint-colors";

/**
 * Component that shows the general settings, including workspace name and server.
 */
export function GeneralSettings(): JSX.Element {
  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);
  const { serverDisplayName, serverIcon } = useAppServer();
  const {
    brandingSettings,
    onChangeBrandingSettings,
    fetchBrandingSettings,
    hasBrandingSettings,
  } = useBrandingSettings();
  const isLoading = useAppSelector(isFetchingSdbCompanySelector);
  const { isUpdatingBrandingSettings } = useAppSelector(
    fetchingSdbCompanyFlagsSelector
  );
  const defaultPlatformSection = useDefaultPlatformSection();
  const { waypointColor, updateWaypointColor } = useWaypointColor();
  const { shouldShowOpenInWebEditorBtn } = useAppSelector(
    defaultProjectLaunchTargetSelector
  );
  const { isBetaTestingEnabled } = useAppSelector(appStateSelector);

  // Fetch branding settings of the company
  useEffect(() => {
    void fetchBrandingSettings();
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only fetch once
  }, []);

  const pageSectionsProps: PageSectionProps[] = [
    {
      title: "Workspace details",
      subtitle:
        "An overview of the details in your workspace.\nHere you can modify the settings to make them fit your needs.",
      items: [
        {
          label: "Workspace name",
          shouldAddRightPadding: true,
          content: (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  paddingRight: "10px",
                  ...withEllipsis,
                }}
              >
                <SphereTooltip
                  title={<var>{selectedSdbCompany?.name}</var>}
                  shouldSkipWrapper={true}
                >
                  <Box
                    component="var"
                    sx={{
                      ...withEllipsis,
                    }}
                  >
                    {selectedSdbCompany?.name}
                  </Box>
                </SphereTooltip>
              </Box>
              <Box>
                <SphereTooltipIcon
                  text="Please contact support to request changes to the workspace name."
                  icon={InfoIcon}
                  size="21px"
                />
              </Box>
            </Box>
          ),
        },
        {
          label: "Server",
          shouldAddRightPadding: true,
          content: (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              <SvgIcon
                inheritViewBox={true}
                component={serverIcon}
                sx={{
                  width: "24px",
                  height: "24px",
                }}
              />
              <Box component="div" marginLeft="10px">
                {serverDisplayName}
              </Box>
            </Box>
          ),
        },
      ],
    },

    // Default platform section
    defaultPlatformSection,

    {
      title: "Default waypoint color",
      subtitle:
        "Select the default color to use for waypoints in Sphere XG when custom colorization is selected.",
      isHidden: !isBetaTestingEnabled,
      items: [
        {
          shouldHideBottomBorder: true,
          shouldHideTopBorder: true,
          shouldContentTakeFullWidth: true,
          content: (
            <ColorPalette
              colors={WAYPOINT_COLOR_PALETTE}
              value={waypointColor}
              onChange={(color) => {
                if (color) {
                  void updateWaypointColor(color);
                }
              }}
            />
          ),
        },
      ],
    },

    {
      title: "HoloBuilder branding",
      additionalInfo: "ONLY IN HOLOBUILDER",
      subtitle: `Configure your company branding including your logo and company URL in the dashboard 
      to be added automatically to all your new and existing company projects.`,
      isHidden:
        !shouldShowOpenInWebEditorBtn || (!isLoading && !hasBrandingSettings),
      items: [
        {
          label: "Workspace URL",
          shouldHidePaddingY: true,
          content: (
            <Box width="100%">
              <FaroTextField
                placeholder="https://example.com/"
                initialValue={brandingSettings.logo.target}
                onConfirmed={(value) =>
                  onChangeBrandingSettings(value, "target")
                }
                isFullWidth={true}
                faroVariant="row"
                isReadOnly={false}
                minInputLength={0}
                sx={{ ...inputPlaceholderItalicSx }}
              />
            </Box>
          ),
        },
        {
          label: "Show HoloBuilder logo",
          shouldAddRightPadding: true,
          shouldKeepContentInlineOnSmallScreens: true,
          content: (
            <Box width="100%" display="flex" justifyContent="end">
              <Box width="50px" display="flex">
                <FaroSwitch
                  disabled={false}
                  isLoading={isUpdatingBrandingSettings}
                  checked={brandingSettings.logo.showHoloBuilderLogo}
                  onChange={(_, isChecked) => {
                    void onChangeBrandingSettings(
                      isChecked,
                      "showHoloBuilderLogo"
                    );
                  }}
                />
              </Box>
            </Box>
          ),
        },
        {
          shouldHideBottomBorder: true,
          shouldContentTakeFullWidth: true,
          content: (
            <Box marginTop="24px" width="100%">
              <WorkspaceLogoSetting />
            </Box>
          ),
        },
      ],
    },
  ];

  return (
    <BaseSettingsPage
      pageSectionProps={pageSectionsProps}
      isLoading={isLoading}
      dataTestId="workspace-settings-general"
      customSx={{ mt: "45px" }}
    />
  );
}
